import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import type { StudioFromGoalDraft } from "@/hooks/useAgentSpecStudioThreads";

export default function StudioFromGoalDialog({
  open,
  pending,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  pending?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (goal: StudioFromGoalDraft) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) {
      setTitle("");
      setDescription("");
    }
  }, [open]);

  const trimmedTitle = title.trim();
  const canSubmit = trimmedTitle.length > 0 && !pending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("agentSpec.studioFromGoalTitle")}</DialogTitle>
          <DialogDescription>
            {t("agentSpec.studioFromGoalDescription")}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            onSubmit({
              title: trimmedTitle,
              description: description.trim() || undefined,
            });
          }}
        >
          <div className="space-y-2">
            <label
              htmlFor="studio-from-goal-title"
              className="text-sm font-medium"
            >
              {t("agentSpec.studioFromGoalTitleLabel")}
            </label>
            <Input
              id="studio-from-goal-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t("agentSpec.studioFromGoalTitlePlaceholder")}
              autoFocus
              disabled={pending}
              required
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="studio-from-goal-description"
              className="text-sm font-medium"
            >
              {t("agentSpec.studioFromGoalDescriptionLabel")}
            </label>
            <textarea
              id="studio-from-goal-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t("agentSpec.studioFromGoalDescriptionPlaceholder")}
              disabled={pending}
              rows={4}
              className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("agentSpec.studioFromGoalCancel")}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {pending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
              ) : null}
              {t(
                pending
                  ? "agentSpec.studioFromGoalPending"
                  : "agentSpec.studioFromGoalConfirm",
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
